import { useEffect, useId, useRef } from 'react'
import PropTypes from 'prop-types'
import { motion, useAnimationControls } from 'framer-motion'

import { useReducedMotionPreference, DURATION, SHAKE_KEYFRAMES } from '../../../styles/motion'
import styles from './OTPInput.module.css'

// Spec §5.5 masking: show the last 2 typed digits; earlier digits become an 8px dot.
const maskDigits = (value, length) =>
  Array.from({ length }, (_, i) => {
    if (i >= value.length) return { kind: 'empty' }
    if (i < value.length - 2) return { kind: 'dot' }
    return { kind: 'digit', value: value[i] }
  })

/**
 * Spec §5.5/§10 — a single real `<input autocomplete="one-time-code">` laid over 6 visual
 * boxes (so paste and SMS autofill work natively), rather than 6 separate real inputs like
 * the legacy OTPInputField.js. The hidden input is stretched over the whole row so clicking
 * anywhere focuses it; the boxes are purely decorative (aria-hidden via the wrapping div
 * having no interactive role of its own).
 */
const OTPInput = ({ value, onChange, length, status, onComplete, disabled, autoFocus }) => {
  const inputRef = useRef(null)
  const rowControls = useAnimationControls()
  const reduceMotion = useReducedMotionPreference()
  const inputId = useId()
  const prevStatusRef = useRef(status)

  useEffect(() => {
    if (status === 'wrong' && prevStatusRef.current !== 'wrong') {
      if (reduceMotion) {
        rowControls.set({ x: 0 })
      } else {
        rowControls.start({ x: SHAKE_KEYFRAMES, transition: { duration: DURATION.wrongCodeShake } })
      }
    }
    prevStatusRef.current = status
  }, [status, rowControls, reduceMotion])

  useEffect(() => {
    if (value.length === length && status === 'default') {
      onComplete?.(value)
    }
  }, [value, length, status, onComplete])

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, length)
    onChange(digits)
  }

  const cells = maskDigits(value, length)
  const activeIndex = Math.min(value.length, length - 1)

  return (
    <div className={styles.wrapper}>
      <motion.div className={styles.boxes} animate={rowControls} aria-hidden='true'>
        {cells.map((cell, index) => {
          const isActive = status === 'default' && index === activeIndex
          const classes = [
            styles.box,
            isActive && styles.active,
            status === 'verified' && styles.verified,
            status === 'wrong' && styles.wrong,
          ]
            .filter(Boolean)
            .join(' ')
          const transitionDelay =
            status === 'verified' && !reduceMotion ? `${index * DURATION.otpVerifiedStagger}s` : '0s'

          return (
            <div key={index} className={classes} style={{ transitionDelay }}>
              {cell.kind === 'dot' && <span className={styles.dot} />}
              {cell.kind === 'digit' && (
                // No key tied to `value` / `value.length` here on purpose: this box's
                // digit only needs to "pop" the moment it first becomes a digit (the
                // dot -> digit conditional swap above already mounts/unmounts it then).
                // Keying on length would force BOTH visible digit boxes to remount on
                // every keystroke even when their content hadn't changed, which is what
                // made typing feel laggy.
                <motion.span
                  initial={reduceMotion ? false : { scale: 0.6 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: DURATION.otpDigitPop }}
                >
                  {cell.value}
                </motion.span>
              )}
            </div>
          )
        })}
      </motion.div>
      <input
        ref={inputRef}
        id={inputId}
        className={styles.hiddenInput}
        type='text'
        inputMode='numeric'
        autoComplete='one-time-code'
        // Sets the real `autofocus` DOM attribute (not just the manual .focus() call
        // below) so BottomSheet's focus trap can detect and prefer this element over the
        // sheet's own first-focusable-element fallback (its close button).
        autoFocus={autoFocus}
        maxLength={length}
        value={value}
        disabled={disabled || status === 'checking' || status === 'verified'}
        aria-label={`Enter the ${length}-digit code`}
        onChange={handleChange}
      />
    </div>
  )
}

OTPInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number,
  status: PropTypes.oneOf(['default', 'checking', 'verified', 'wrong']),
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
}

OTPInput.defaultProps = {
  length: 6,
  status: 'default',
  disabled: false,
  autoFocus: false,
}

export default OTPInput
